import { useState, useEffect, useRef } from 'react';
import { useStickyMobileBar } from '../../../context/StickyMobileBarContext';
import { formatCurrency } from '../../../utils/currency';
import './StickyMobileBar.css';

/**
 * StickyMobileBar — Product page only
 * 
 * Watches the main Add to Cart button (#main-atc) via IntersectionObserver.
 * When the button scrolls out of view, this bar slides up with product name,
 * price, and a full-width Add to Cart button.
 * 
 * Only renders when barData is set (product page active).
 */
function StickyMobileBar() {
  const { barData } = useStickyMobileBar();
  const [isVisible, setIsVisible] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    // Only watch if we have product data
    if (!barData) {
      setIsVisible(false);
      return;
    }

    const mainAtc = document.getElementById('main-atc');
    if (!mainAtc) {
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When main ATC is NOT visible (scrolled out of view), show sticky bar
        setIsVisible(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    observer.observe(mainAtc);

    return () => {
      observer.disconnect();
    };
  }, [barData]);

  // Don't render if no product data
  if (!barData) return null;

  const { productName, price, onAddToCart, canAddToCart, buttonText } = barData;

  return (
    <div
      ref={barRef}
      className={`sticky-mobile-bar${isVisible ? ' sticky-mobile-bar--visible' : ''}`}
      role="complementary"
      aria-label="Add to cart"
    >
      <div className="sticky-mobile-bar__content">
        <div className="sticky-mobile-bar__info">
          <span className="sticky-mobile-bar__name">{productName}</span>
          <span className="sticky-mobile-bar__price">{formatCurrency(price)}</span>
        </div>
        <button
          className="sticky-mobile-bar__cta"
          onClick={onAddToCart}
          disabled={!canAddToCart}
          aria-label={`Add ${productName} to cart`}
        >
          {buttonText || 'Add to Bag'}
        </button>
      </div>
    </div>
  );
}

export default StickyMobileBar;
